import { useCallback, useEffect, useRef, useState } from "react";
import { FaCamera, FaCropAlt, FaPaperPlane, FaTrash } from "react-icons/fa";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import {
  azurecycleAkanthasSendBusqueueRequest,
  getSuperContainerList,
  saveContainerWeight,
  writeazureImage,
  writeazureImageLog,
} from "../services/container";
import { useWasteMetadataSelectOptions } from "./photoCapture/useWasteMetadataSelectOptions";
import AppNavBar from "./AppNavBar";
import AppHeaderBar from "./AppHeaderBar";
import PhotoCaptureCropEditor from "./photoCapture/PhotoCaptureCropEditor";
import MobileForbiddenModule from "./MobileForbiddenModule";
import { hasPhotoCaptureModule } from "./mobileModuleUtils";
import {
  applyPolygonWhiteMask,
  buildDefaultPhotoCropPoints,
  canApplyPhotoCrop,
} from "./photoCapture/photoCaptureCropUtils";
import PhotoCaptureMetadataEntry from "./photoCapture/PhotoCaptureMetadataEntry";
import PhotoCaptureMetadataPage from "./photoCapture/PhotoCaptureMetadataPage";
import PhotoCaptureSendOverlay from "./photoCapture/PhotoCaptureSendOverlay";
import {
  buildCaptureTimestampPair,
  buildClientPayloadForOutput,
  buildOutputClientBlobName,
  buildOutputClientPayload,
  buildPhotoCaptureCommentsList,
  buildRawImageBlobName,
  resolveClientMeasurementContainerId,
  resolveMobileCameraContainerId,
  resolveWeightKgFromTonnesInput,
  shouldWriteOutputClient,
  tryParseSelectedWasteCategory,
} from "./photoCapture/photoCaptureMobileUtils";
import { createCamera, getCameraData } from "../services/camera";

const STEP_PICK = "pick";
const STEP_CROP_CHOICE = "crop_choice";
const STEP_CROP = "crop";
const PHOTO_CAPTURE_FILE_INPUT_ID = "fileInput";

const AppCameraCapture = ({ setCookies, saveUser }) => {
  const { t } = useTranslation();
  const history = useHistory();
  const [metadataOpen, setMetadataOpen] = useState(false);
  const [metadataFieldSheetOpen, setMetadataFieldSheetOpen] = useState(false);
  const [wizardStep, setWizardStep] = useState(STEP_PICK);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [originalImageBase64, setOriginalImageBase64] = useState(null);
  const [imageBase64, setImageBase64] = useState(null);
  const [cropPoints, setCropPoints] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isApplyingCrop, setIsApplyingCrop] = useState(false);
  const photoCaptureEnabled = hasPhotoCaptureModule();
  const {
    clientOptions,
    wasteCategoryOptions,
    isLoading: isLoadingOptions,
    loadError,
  } = useWasteMetadataSelectOptions(photoCaptureEnabled);
  const [selectedClient, setSelectedClient] = useState("");
  const [selectedWasteCategory, setSelectedWasteCategory] = useState("");
  const [identificationReference, setIdentificationReference] = useState("");
  const [weightTonnes, setWeightTonnes] = useState("");
  const [metadataComment, setMetadataComment] = useState("");
  const [sendStatus, setSendStatus] = useState(null);
  const fileInputRef = useRef(null);
  const previewImageRef = useRef(null);

  const corporationModules = JSON.parse(
    localStorage.getItem("corporationModules"),
  );
  const cameraId = `ak_mobile_${corporationModules._id.toLowerCase()}`;

  const defaultCameraData = {
    id: cameraId,
    iccid: "",
    active: true,
    address: null,
    solar_panel_id: "",
    status: "working",
    manualReviewOnly: true,
    corporation: corporationModules._id,
    hardwareComponents: null,
    nbContainers: 1,
  };

  const revokePreviewUrl = useCallback((url) => {
    if (url?.startsWith("blob:")) {
      URL.revokeObjectURL(url);
    }
  }, []);

  useEffect(() => {
    if (loadError) {
      toast.error(t("mobile_photo_fields_load_error"));
    }
  }, [loadError, t]);

  const resetWizard = () => {
    revokePreviewUrl(previewUrl);
    setPreviewUrl(null);
    setOriginalImageBase64(null);
    setImageBase64(null);
    setCropPoints([]);
    setSelectedClient("");
    setSelectedWasteCategory("");
    setIdentificationReference("");
    setWeightTonnes("");
    setMetadataComment("");
    setSendStatus(null);
    setMetadataOpen(false);
    setMetadataFieldSheetOpen(false);
    setWizardStep(STEP_PICK);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const ensureMobileCameraWithSingleContainer = async () => {
    let { data: { data: camera } = {} } = await getCameraData(cameraId);

    if (!camera) {
      await createCamera(defaultCameraData);
      ({ data: { data: camera } = {} } = await getCameraData(cameraId));
    }

    const resolved = resolveMobileCameraContainerId(camera);

    if (resolved.error === "none") {
      console.error(
        "[mobile-photo] Camera has no container:",
        cameraId,
        camera?.containerObjectIdList,
      );
      throw new Error("mobile_photo_container_none");
    }

    if (resolved.error === "multiple") {
      console.error(
        "[mobile-photo] Camera has multiple containers:",
        cameraId,
        resolved.containerIds,
      );
      throw new Error("mobile_photo_container_multiple");
    }

    return resolved.containerId;
  };

  const onFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file?.type.startsWith("image/")) {
      return;
    }

    revokePreviewUrl(previewUrl);

    const reader = new FileReader();
    reader.onloadend = () => {
      const dataUrl = reader.result;
      setOriginalImageBase64(dataUrl);
      setImageBase64(dataUrl);
      setPreviewUrl(dataUrl);
      setCropPoints([]);
      setWizardStep(STEP_CROP_CHOICE);
    };
    reader.readAsDataURL(file);
  };

  const savePhoto = async (imagePayload) => {
    const payload = imagePayload ?? imageBase64;
    if (!payload) {
      return;
    }

    try {
      setIsLoading(true);
      setSendStatus("loading");

      const physicalContainerId = await ensureMobileCameraWithSingleContainer();
      let superContainers = [];
      try {
        const superContainerResponse = await getSuperContainerList();
        superContainers = superContainerResponse?.data?.superContainers ?? [];
      } catch (error) {
        console.error("[mobile-photo] Failed to load super containers:", error);
      }
      const containerId = resolveClientMeasurementContainerId(
        physicalContainerId,
        superContainers,
      );
      const { captureTime, fileTimestamp } = buildCaptureTimestampPair();
      const { blobName, imageName } = buildRawImageBlobName(
        cameraId,
        fileTimestamp,
      );
      const outputClientBlobName = buildOutputClientBlobName(
        cameraId,
        fileTimestamp,
        containerId,
      );
      await writeazureImage(cameraId, {
        blobName,
        output: JSON.stringify({ imageBase64: payload }),
      });

      if (
        shouldWriteOutputClient({
          selectedClientJson: selectedClient,
          selectedWasteCategoryJson: selectedWasteCategory,
          identificationReference,
          weightInput: weightTonnes,
          commentText: metadataComment,
        })
      ) {
        const clientPayload = buildClientPayloadForOutput({
          selectedClientJson: selectedClient,
          identificationReference,
        });
        await saveContainerWeight(
          buildOutputClientPayload({
            containerId,
            captureTime,
            blobName: outputClientBlobName,
            clientPayload: clientPayload.clientPayload,
            wasteCategoryPayload:
              tryParseSelectedWasteCategory(selectedWasteCategory) ?? "",
            mongoClientId: clientPayload.mongoClientId,
            weightKg: resolveWeightKgFromTonnesInput(weightTonnes),
            comments: buildPhotoCaptureCommentsList(metadataComment),
          }),
        );
      }

      await azurecycleAkanthasSendBusqueueRequest(
        { queueName: "contourQueue" },
        imageName,
      );
      await writeazureImageLog(cameraId, {
        blobName,
        output: JSON.stringify({ imageBase64: payload }),
      });

      setSendStatus("success");
      await new Promise((resolve) => {
        setTimeout(resolve, 2000);
      });
      resetWizard();
      toast.success(t("mobile_photo_success"));
    } catch (error) {
      setSendStatus(null);
      if (error?.message === "mobile_photo_container_none") {
        toast.error(t("mobile_photo_container_none"));
      } else if (error?.message === "mobile_photo_container_multiple") {
        toast.error(t("mobile_photo_container_multiple"));
      } else {
        toast.error(t("mobile_photo_error"));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleApplyCrop = async () => {
    if (!canApplyPhotoCrop(cropPoints)) {
      toast.error(t("mobile_photo_crop_min_points"));
      return;
    }

    const source = originalImageBase64 || imageBase64;
    if (!source) {
      return;
    }

    setIsApplyingCrop(true);
    try {
      const croppedDataUrl = await applyPolygonWhiteMask(source, cropPoints);
      setImageBase64(croppedDataUrl);
      setPreviewUrl(croppedDataUrl);
      setCropPoints([]);
      setWizardStep(STEP_CROP_CHOICE);
    } catch {
      toast.error(t("mobile_photo_crop_error"));
    } finally {
      setIsApplyingCrop(false);
    }
  };

  const isBusy = isLoading || isApplyingCrop;
  const isPickStep = wizardStep === STEP_PICK;
  const isChoiceStep = wizardStep === STEP_CROP_CHOICE;
  const isCropStep = wizardStep === STEP_CROP;
  const cropEditorImageUrl = originalImageBase64 || previewUrl;
  const previewImageUrl = imageBase64 || previewUrl;
  const isCroppedPreview =
    Boolean(originalImageBase64) &&
    Boolean(imageBase64) &&
    imageBase64 !== originalImageBase64;
  const canSendPhoto = !isBusy && !isLoadingOptions && !sendStatus;

  const cancelCrop = () => {
    setCropPoints([]);
    setWizardStep(STEP_CROP_CHOICE);
  };

  const startCrop = () => {
    const imageElement = previewImageRef.current;
    if (imageElement?.naturalWidth > 0 && imageElement?.naturalHeight > 0) {
      setCropPoints(
        buildDefaultPhotoCropPoints(
          imageElement.naturalWidth,
          imageElement.naturalHeight,
        ),
      );
    } else {
      setCropPoints([]);
    }
    setWizardStep(STEP_CROP);
  };

  const isFlowStep = !isPickStep && photoCaptureEnabled;

  const pageClassName = [
    "installation-list-page",
    "photo-capture-page",
    isPickStep && "photo-capture-page-pick",
    isFlowStep && "photo-capture-page-flow",
    metadataOpen && "photo-capture-page-metadata",
    sendStatus && "photo-capture-page-sending",
  ]
    .filter(Boolean)
    .join(" ");

  const bodyCardClassName = [
    "photo-capture-body-card",
    isPickStep && "photo-capture-body-card-pick",
  ]
    .filter(Boolean)
    .join(" ");

  const closeMetadataPage = useCallback(() => {
    setMetadataOpen(false);
    setMetadataFieldSheetOpen(false);
  }, []);

  const openMetadataPage = useCallback(() => {
    if (wizardStep === STEP_CROP_CHOICE && (imageBase64 || previewUrl)) {
      setMetadataOpen(true);
    }
  }, [imageBase64, previewUrl, wizardStep]);

  const handleMetadataBack = useCallback(
    ({
      selectedClient: nextClient,
      selectedWasteCategory: nextWasteCategory,
      identificationReference: nextReference,
      weightTonnes: nextWeight,
      comment: nextComment = "",
    }) => {
      setSelectedClient(nextClient);
      setSelectedWasteCategory(nextWasteCategory);
      setIdentificationReference(nextReference);
      setWeightTonnes(nextWeight);
      setMetadataComment(nextComment);
      closeMetadataPage();
    },
    [closeMetadataPage],
  );

  useEffect(() => {
    if (!metadataOpen) {
      return undefined;
    }

    const unlisten = history.listen((location) => {
      if (location.pathname !== "/app/photo") {
        setMetadataOpen(false);
      }
    });

    return () => unlisten();
  }, [history, metadataOpen]);

  useEffect(() => {
    if (!metadataOpen) {
      return undefined;
    }

    const unblock = history.block((_location, action) => {
      if (action === "POP") {
        setMetadataOpen(false);
        return false;
      }
      return undefined;
    });

    return () => unblock();
  }, [history, metadataOpen]);

  useEffect(() => {
    if (metadataOpen && wizardStep !== STEP_CROP_CHOICE) {
      closeMetadataPage();
    }
  }, [closeMetadataPage, metadataOpen, wizardStep]);

  useEffect(() => {
    if (!metadataOpen) {
      setMetadataFieldSheetOpen(false);
    }
  }, [metadataOpen]);

  return (
    <div className={pageClassName}>
      <AppHeaderBar
        title={t("Photo")}
        setCookies={setCookies}
        saveUser={saveUser}
      />

      <main className="photo-capture-main">
        {!photoCaptureEnabled && (
          <div className="installation-list-shell">
            <MobileForbiddenModule viewType="photoCapture" />
          </div>
        )}

        {photoCaptureEnabled && metadataOpen && (
          <PhotoCaptureMetadataPage
            key={`metadata-${previewImageUrl ?? "empty"}`}
            initialClient={selectedClient}
            initialWasteCategory={selectedWasteCategory}
            initialReference={identificationReference}
            initialWeight={weightTonnes}
            initialComment={metadataComment}
            onBack={handleMetadataBack}
            previewImageUrl={previewImageUrl}
            clientOptions={clientOptions}
            wasteCategoryOptions={wasteCategoryOptions}
            disabled={isBusy}
            onActiveFieldSheetChange={setMetadataFieldSheetOpen}
          />
        )}

        {photoCaptureEnabled && !metadataOpen && (
          <>
            {isChoiceStep && previewImageUrl && (
              <>
                <div className="installation-subpage-card photo-capture-choice-card photo-capture-choice-card-flush">
                  <div
                    className={
                      isCroppedPreview
                        ? "photo-capture-choice-stage photo-capture-choice-stage-cropped"
                        : "photo-capture-choice-stage"
                    }
                  >
                    <img
                      ref={previewImageRef}
                      src={previewImageUrl}
                      alt={t("mobile_photo_preview_alt")}
                      className="photo-capture-choice-img"
                    />
                    <div className="photo-capture-choice-actions">
                      <button
                        type="button"
                        className="photo-capture-choice-fab photo-capture-choice-fab-discard"
                        onClick={resetWizard}
                        disabled={isBusy}
                        aria-label={t("mobile_photo_discard")}
                      >
                        <FaTrash aria-hidden />
                      </button>
                      <button
                        type="button"
                        className="photo-capture-choice-fab photo-capture-choice-fab-crop"
                        onClick={startCrop}
                        disabled={isBusy}
                        aria-label={t("mobile_photo_action_crop")}
                      >
                        <FaCropAlt aria-hidden />
                      </button>
                      <button
                        type="button"
                        className="photo-capture-choice-fab photo-capture-choice-fab-send"
                        onClick={() => void savePhoto()}
                        disabled={!canSendPhoto}
                        aria-label={t("mobile_photo_send")}
                      >
                        <FaPaperPlane aria-hidden />
                      </button>
                    </div>
                  </div>
                </div>

                <PhotoCaptureMetadataEntry
                  clientOptions={clientOptions}
                  wasteCategoryOptions={wasteCategoryOptions}
                  selectedClient={selectedClient}
                  selectedWasteCategory={selectedWasteCategory}
                  identificationReference={identificationReference}
                  weightTonnes={weightTonnes}
                  commentText={metadataComment}
                  onOpen={openMetadataPage}
                  disabled={isBusy}
                  isLoadingOptions={isLoadingOptions}
                />
              </>
            )}

            {isCropStep && cropEditorImageUrl && (
              <div className="photo-capture-choice-card photo-capture-choice-card-flush">
                <div className="photo-capture-choice-stage">
                  <PhotoCaptureCropEditor
                    key={cropEditorImageUrl}
                    imageUrl={cropEditorImageUrl}
                    draftPoints={cropPoints}
                    onDraftPointsChange={setCropPoints}
                    disabled={isBusy}
                  />
                </div>
              </div>
            )}

            {isPickStep && (
              <div className={bodyCardClassName}>
                <div className="photo-capture-pick-area">
                  <label
                    htmlFor={isBusy ? undefined : PHOTO_CAPTURE_FILE_INPUT_ID}
                    className={
                      isBusy
                        ? "photo-capture-pick-trigger photo-capture-pick-trigger-disabled"
                        : "photo-capture-pick-trigger"
                    }
                    aria-disabled={isBusy}
                  >
                    <span className="photo-capture-pick-icon" aria-hidden>
                      <FaCamera />
                    </span>
                    <span className="photo-capture-pick-label">
                      {t("mobile_photo_pick")}
                    </span>
                    <span className="photo-capture-pick-hint">
                      {t("mobile_photo_pick_hint")}
                    </span>
                  </label>
                  <input
                    id={PHOTO_CAPTURE_FILE_INPUT_ID}
                    ref={fileInputRef}
                    type="file"
                    accept="image/*,application/json"
                    hidden
                    onChange={onFileChange}
                  />
                </div>
              </div>
            )}

            {wizardStep === STEP_CROP && !isBusy && (
              <div className="photo-capture-wizard-actions photo-capture-wizard-actions-split">
                <button
                  type="button"
                  className="photo-capture-wizard-secondary"
                  onClick={cancelCrop}
                >
                  {t("mobile_photo_cancel_crop")}
                </button>
                <button
                  type="button"
                  className="installation-save-sheet-button photo-capture-wizard-primary"
                  onClick={() => void handleApplyCrop()}
                  disabled={!canApplyPhotoCrop(cropPoints)}
                >
                  {isApplyingCrop
                    ? t("mobile_photo_crop_applying")
                    : t("mobile_photo_validate")}
                </button>
              </div>
            )}
          </>
        )}
      </main>

      {photoCaptureEnabled && <PhotoCaptureSendOverlay status={sendStatus} />}

      {photoCaptureEnabled && !metadataFieldSheetOpen && <AppNavBar />}
    </div>
  );
};

export default AppCameraCapture;
